import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { suggestLocations } from "../lib/ukLocations";

export default function LocationInput({ value, onChange, placeholder = "Town, city or postcode", testid, className = "", icon = true, liveLocations = null }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const suggestions = (() => {
    // static UK towns/cities/postcodes first, then any live job locations on top
    const base = suggestLocations(value);
    if (!liveLocations) return base;
    const q = value.trim().toLowerCase();
    const live = liveLocations.filter((l) => !q || l.toLowerCase().includes(q));
    return [...new Set([...base, ...live])].slice(0, 8);
  })();

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (loc) => {
    onChange(loc);
    setOpen(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter" && highlight >= 0) {
      e.preventDefault();
      pick(suggestions[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      {icon && <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none z-10" />}
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className={`w-full h-12 ${icon ? "pl-11" : "pl-4"} pr-4 bg-white/80 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent`}
      />
      {open && suggestions.length > 0 && (
        <ul data-testid={`${testid}-suggestions`}
          className="absolute z-[80] left-0 right-0 mt-1.5 bg-white border border-zinc-200 rounded-md shadow-xl overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((loc, i) => (
            <li key={loc}>
              <button
                type="button"
                data-testid={`${testid}-option-${loc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onMouseDown={(e) => { e.preventDefault(); pick(loc); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors duration-100 ${
                  i === highlight ? "bg-zinc-100 text-zinc-950" : "text-zinc-700"}`}
              >
                <MapPin className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                {loc}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
